<template>
  <div class="radioTitle" id="required-questions">
    <span v-html="content"></span>
    <span v-if="isRequired" class="required" v-html="requiredMessage"></span>
  </div>
  <el-radio-group
    v-model="computedSelectedOption"
    @change="onChange"
    class="radioGroup"
  >
    <el-radio
      class="menstrualPhaseRadio"
      :key="options[0].value"
      :label="options[0].value"
      border
      :disabled="this.disabled"
    >
      {{ options[0].label }}
    </el-radio>
    <div
      class="expandedContainer"
      v-if="
        this.hormoneZoomerQnr.basic_info.reproductive_health_stage ===
        'PREMENOPAUSAL_PHASE'
      "
    >
      <QuestionnaireRadio
        content="<span class='font-normal'>Please indicate menstrual phase on day of sample collection below </span><i><span style='font-weight: bold;'>if Premenopausal</span></i>"
        width="240px"
        mobileWidth="285px"
        :options="premenopausalPhaseResultOptions"
        :isRequired="true"
        requiredMessage=" *Required"
        :value="this.hormoneZoomerQnr.basic_info.current_menstrual_phase"
        :disabled="this.qnrIsCompleted"
        @onChange="
          handleValueChange('basic_info.current_menstrual_phase', $event)
        "
      />
    </div>
    <el-radio
      class="menstrualPhaseRadio"
      :key="options[1].value"
      :label="options[1].value"
      border
      :disabled="this.disabled"
    >
      {{ options[1].label }}
    </el-radio>
    <el-radio
      class="menstrualPhaseRadio"
      :key="options[2].value"
      :label="options[2].value"
      border
      :disabled="this.disabled"
    >
      {{ options[2].label }}
    </el-radio>
    <div
      class="expandedContainer"
      v-if="
        this.hormoneZoomerQnr.basic_info.reproductive_health_stage ===
          'POSTMENOPAUSAL_PHASE' ||
        this.hormoneZoomerQnr.basic_info.reproductive_health_stage ===
          'REPLACEMENT_THERAPY'
      "
    >
      <QuestionnaireRadio
        content="<span class='font-normal'>Please indicate if you're using hormone replacement therapy (HRT)below </span><i><span style='font-weight: bold;'>if Postmenopausal</span></i>"
        mobileWidth="140px"
        width="120px"
        :options="yesNoOptions"
        :isRequired="true"
        requiredMessage=" * Required"
        :value="this.usingHRT"
        :disabled="this.qnrIsCompleted"
        @onChange="handleUsingHRTChange($event)"
      />
    </div>
    <el-radio
      class="menstrualPhaseRadio"
      :key="options[3].value"
      :label="options[3].value"
      border
      :disabled="this.disabled"
    >
      {{ options[3].label }}
    </el-radio>
    <el-radio
      class="menstrualPhaseRadio"
      :key="options[4].value"
      :label="options[4].value"
      border
      :disabled="this.disabled"
    >
      {{ options[4].label }}
    </el-radio>
  </el-radio-group>
</template>

<script>
import QuestionnaireRadio from "./QuestionnaireRadio.vue";
import { useHormoneZoomerStore } from "@/stores/HormoneZoomerStore.ts";
import { mapState, mapActions } from "pinia";
import {
  yesNoOptions,
  premenopausalPhaseResultOptions,
} from "@/utils/questionnaireOptions";

export default {
  name: "MenstrualPhaseRadio",
  components: {
    QuestionnaireRadio,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
    },
    content: {
      type: String,
      required: true,
    },
    height: {
      type: String,
    },
    width: {
      type: String,
    },
    mobileWidth: {
      type: String,
    },
    options: {
      type: Array,
      required: true,
    },
    requiredMessage: {
      type: String,
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.selectedOption = newValue;
      },
    },
    reproductiveHealthStage: {
      immediate: true,
      handler: function (newValue) {
        this.value = newValue;
        this.selectedOption = newValue;
        if (newValue !== "PREMENOPAUSAL_PHASE") {
          this.setHormoneZoomerQnrSingleField(
            "basic_info.current_menstrual_phase",
            null
          );
        }
        if (
          newValue !== "POSTMENOPAUSAL_PHASE" &&
          newValue !== "REPLACEMENT_THERAPY"
        ) {
          this.setUsingHRT(null);
        }
      },
    },
  },
  emits: ["onChange", "removeWarning"],
  data() {
    return {
      value: "",
      selectedOption: "",
      radioHeight: this.height === "tall" ? "80px" : "42px",
      radioWidth: this.width === "wide" ? "27vw" : this.width,
      yesNoOptions,
      premenopausalPhaseResultOptions,
    };
  },
  computed: {
    ...mapState(useHormoneZoomerStore, [
      "hormoneZoomerQnr",
      "qnrIsCompleted",
      "usingHRT",
    ]),
    reproductiveHealthStage() {
      return this.hormoneZoomerQnr?.basic_info?.reproductive_health_stage;
    },
    computedSelectedOption: {
      get() {
        if (this.selectedOption === 'REPLACEMENT_THERAPY') {
          return 'POSTMENOPAUSAL_PHASE';
        }
        return this.selectedOption;
      },
      set(value) {
        this.selectedOption = value;
      }
    },
  },
  methods: {
    ...mapActions(
      () => useHormoneZoomerStore(),
      ["setHormoneZoomerQnrSingleField", "setUsingHRT"]
    ),
    onChange() {
      this.$emit("onChange", this.selectedOption);
      this.$emit("removeWarning");
    },
    handleUsingHRTChange(value) {
      this.setUsingHRT(value);
      this.$emit("removeWarning");
    },
    handleValueChange(key, value) {
      this.setHormoneZoomerQnrSingleField(key, value);
      if (
        key === "basic_info.reproductive_health_stage" ||
        key === "basic_info.current_menstrual_phase"
      ) {
        this.$emit("removeWarning");
      }
    },
  },
};
</script>

<style scoped>
.radioTitle {
  font-size: 16px;
  font-weight: 600;
  line-height: 19px;
  letter-spacing: 0.175px;
  white-space: pre-line;
}

.required {
  color: #ff1d00;
  line-height: 20px;
  font-weight: 400;
}

.el-radio-group {
  display: flex;
  flex-direction: column;
  /* gap:15px; */
  padding: 0px;
  box-sizing: border-box;
  gap: 12px;
  align-items: flex-start;
}

:deep(.menstrualPhaseRadio.el-radio.is-bordered) {
  width: v-bind("radioWidth");
  min-height: v-bind("radioHeight");
  height: fit-content;
  border-radius: 4px;
  border: 1px solid #bcccdc;
  margin-right: 0px;
  background-color: #ffffff;
}

:deep(.el-radio.is-bordered.is-disabled) {
  background-color: #f2f2f2;
  border: 1px solid #bcccdc;
}

:deep(.el-radio__label) {
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.46px;
  color: #000000;
  white-space: normal;
}

:deep(.el-radio.is-bordered.is-checked) {
  border: 1px solid #20a8c3;
  background-color: rgba(32, 168, 195, 0.1);
}

:deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #20a8c3 !important;
}

:deep(.el-radio__input.is-disabled + span.el-radio__label) {
  color: #000000;
}

:deep(.el-radio__inner) {
  height: 20px !important;
  width: 20px !important;
  border: 2px #000000 solid;
  margin-left: 5px;
  margin-right: 5px;
}

:deep(.el-radio__input.is-disabled .el-radio__inner) {
  border: 2px #000000 solid;
  background: transparent !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner) {
  border: 2px #20a8c3 solid !important;
  background: transparent !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner::after) {
  content: "";
  display: block;
  width: 10px;
  height: 10px;
  background-color: #20a8c3;
  border-radius: 50%;
  box-sizing: border-box;
  border: 2px #20a8c3 solid !important;
}

.expandedContainer {
  padding: 12px 24px;
  border-radius: 4px;
  background-color: #f3f6f8;
  width: 808px;
}

@media only screen and (max-width: 808px) {
  .el-radio-group {
    gap: 8px;
  }

  .expandedContainer {
    padding: 12px 16px;
    font-size: 14px;
    width: 334px;
  }

  :deep(.menstrualPhaseRadio.el-radio.is-bordered) {
    min-height: 42px;
    height: fit-content;
    width: v-bind("mobileWidth");
  }

  .radioTitle {
    font-size: 14px;
  }

  :deep(.el-radio__label) {
    font-size: 14px;
  }

  .el-radio div {
    font-size: 14px !important;
    font-weight: 400 !important;
  }

  :deep(.el-radio__inner) {
    height: 16px !important;
    width: 16px !important;
    margin-left: 8px;
    margin-right: 8px;
    border: 1px #000000 solid !important;
  }

  :deep(.el-radio__input.is-checked .el-radio__inner::after) {
    width: 8px;
    height: 8px;
    border: 1px #20a8c3 solid !important;
  }

  span.required {
    font-size: 14px;
    line-height: 16px;
  }
}
</style>
